import { createSlice } from "@reduxjs/toolkit";
import cancelAppointmentFromDb from "../apis/cancelAppointment";
import { saveAppointmentsToDb } from "../apis/saveAppointmentsToDb"; // Adjust path
import { cancelTestFromDb } from "../apis/cancelTestFromDb"; // adjust path as needed
import uploadLabFiles from "../apis/uploadLabFiles";
import { fetchAppointmentsByPatient } from "../apis/normalUser/fetchAppointmentsByPatient";
import referTestToLab from "../apis/Businessuser/testRecommendationThunk";
const initialState = {
  allAppointments: [],
  latestAppointment: null,
  saveStatus: "idle",
  saveError: null,
};

const appointmentsSlice = createSlice({
  name: "appointments",
  initialState,
  reducers: {
    // New reducer to set all appointments (e.g. from fetch)
    setAppointments: (state, action) => {
      if (!Array.isArray(action.payload)) {
        console.error(
          "❌ Invalid appointments payload. Expected array, got:",
          action.payload,
        );
        state.allAppointments = [];
        state.latestAppointment = null;
        return;
      }

      state.allAppointments = action.payload;
      state.latestAppointment =
        action.payload.length > 0
          ? action.payload[action.payload.length - 1]
          : null;
    },

    addnewAppointment: (state, action) => {
      const newAppointment = {
        ...action.payload,
        prescriptions: [],
        testRecommendations: [],
      };
      state.allAppointments.push(newAppointment);
      state.latestAppointment = newAppointment;
    },

    cancelAppointment: (state, action) => {
      const id = action.payload;
      // Assuming appointmentId is at root of each appointment object
      state.allAppointments = state.allAppointments.filter(
        (a) => a.appointmentId !== id,
      );
    },

    cancelTest: (state, action) => {
      const { appointmentId, testId } = action.payload;
      state.allAppointments = state.allAppointments.map((a) => {
        if (a.appointmentId === appointmentId) {
          const filteredTests = a.testRecommendations?.filter(
            (t) => (t.testid || t._id) !== testId,
          );
          return {
            ...a,
            testRecommendations: filteredTests,
          };
        }
        return a;
      });
    },

    rescheduleAppointment: (state, action) => {
      const { id, newDate, newTime } = action.payload;
      state.allAppointments = state.allAppointments.map((a) => {
        if (a.appointmentId === id) {
          return {
            ...a,
            previousDate: a.date,
            previousTime: a.time,
            date: newDate,
            time: newTime,
          };
        }
        return a;
      });
    },

    updateTestRecommendation: (state, action) => {
      const { appointmentId, testid, updatedFields } = action.payload;

      if (!Array.isArray(state.allAppointments)) {
        console.error(
          "❌ allAppointments is not an array:",
          state.allAppointments,
        );
        return;
      }

      state.allAppointments = state.allAppointments.map((appt) => {
        if (appt.appointmentId !== appointmentId) return appt;

        const testRecommendations = Array.isArray(appt.testRecommendations)
          ? appt.testRecommendations
          : [];

        const updatedTests = testRecommendations.map((test) => {
          if (test.testid !== testid) return test;

          return {
            ...test,
            ...(updatedFields || {}), // ✅ Apply new/updated fields
          };
        });

        // Optional debug: warn if testid was not found
        const found = testRecommendations.some((t) => t.testid === testid);
        if (!found) {
          console.warn(
            `⚠️ Test with ID ${testid} not found in appointment ${appointmentId}`,
          );
        }

        return {
          ...appt,
          testRecommendations: updatedTests,
        };
      });
    },

    updateAppointmentStatus: (state, action) => {
      const { id, newStatus } = action.payload;
      state.allAppointments = state.allAppointments.map((a) =>
        a._id === id || a.appointmentId === id
          ? { ...a, status: newStatus }
          : a,
      );
    },

    addPrescription: (state, action) => {
      const { id, prescription } = action.payload;
      state.allAppointments = state.allAppointments.map((a) => {
        if (a._id === id || a.appointmentId === id) {
          return {
            ...a,
            prescriptions: [...(a.prescriptions || []), prescription],
          };
        }
        return a;
      });
    },

    testRecommendation: (state, action) => {
      const {
        id,
        testType,
        doctorDescription,
        otherMedicalService,
        OtherMedicalUser,
      } = action.payload;

      const appointmentIndex = state.allAppointments.findIndex(
        (a) => a.appointmentId === id,
      );

      if (appointmentIndex !== -1) {
        const appointment = state.allAppointments[appointmentIndex];
        const currentTests = appointment.testRecommendations || [];

        const nextTestNumber = currentTests.length + 1;
        const paddedNumber = String(nextTestNumber).padStart(3, "0");
        const combinedTestId = `${appointment.appointmentId}-${paddedNumber}`;

        const newTest = {
          testid: combinedTestId,
          testType,
          doctorDescription,
          ...(otherMedicalService ? { otherMedicalService } : {}),
          ...(OtherMedicalUser
            ? { OtherMedicalUser: { ...OtherMedicalUser } }
            : {}),
        };

        const updatedTestRecommendations = [...currentTests, newTest];

        const updatedAppointment = {
          ...appointment,
          testRecommendations: updatedTestRecommendations,
        };

        state.allAppointments.splice(appointmentIndex, 1, updatedAppointment);
      }
    },

    referToAnotherDoctor: (state, action) => {
      const { id, doctorId } = action.payload;
      state.allAppointments = state.allAppointments.map((a) =>
        a._id === id ? { ...a, referredDoctorId: doctorId } : a,
      );
    },

    updateReferredUser: (state, action) => {
      const { id, newReferredUser } = action.payload;
      state.allAppointments = state.allAppointments.map((a) =>
        a._id === id || a.appointmentId === id
          ? { ...a, referredUser: newReferredUser }
          : a,
      );
    },

    assignUserToTest: (state, action) => {
      const { appointmentId, testid, OtherMedicalUser } = action.payload;
      state.allAppointments = state.allAppointments.map((a) => {
        if (a.appointmentId !== appointmentId) return a;

        const updatedTests = (a.testRecommendations || []).map((test) =>
          test.testid === testid
            ? {
                ...test,
                OtherMedicalUser: { ...OtherMedicalUser },
              }
            : test,
        );

        return {
          ...a,
          testRecommendations: updatedTests,
        };
      });
    },

    // Update an entire appointment partially
    updatedAppointment: (state, action) => {
      const { appointmentId, updatedFields } = action.payload;
      state.allAppointments = state.allAppointments.map((appt) => {
        if (appt.appointmentId === appointmentId) {
          return {
            ...appt,
            ...updatedFields,
          };
        }
        return appt;
      });

      if (state.latestAppointment?.appointmentId === appointmentId) {
        state.latestAppointment = {
          ...state.latestAppointment,
          ...updatedFields,
        };
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(referTestToLab.fulfilled, (state, action) => {
        const { appointmentId, testid, updatedFields } = action.payload;

        state.allAppointments = state.allAppointments.map((appt) => {
          if (appt.appointmentId !== appointmentId) return appt;

          const updatedTests = (appt.testRecommendations || []).map((test) => {
            if (test.testid !== testid) return test;
            return {
              ...test,
              ...updatedFields,
            };
          });

          return {
            ...appt,
            testRecommendations: updatedTests,
          };
        });
      })
      .addCase(referTestToLab.rejected, (state, action) => {
        console.error("❌ Failed to refer test:", action.payload);
      })

      .addCase(fetchAppointmentsByPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointmentsByPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.allAppointments = action.payload;
      })
      .addCase(fetchAppointmentsByPatient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(uploadLabFiles.fulfilled, (state, action) => {
        const { appointmentId, testId, labFiles, labDescription } =
          action.payload;

        state.allAppointments = state.allAppointments.map((appt) => {
          if (appt.appointmentId !== appointmentId) return appt;

          const updatedTests = (appt.testRecommendations || []).map((test) =>
            test.testid === testId
              ? {
                  ...test,

                  // ✅ append files (not overwrite)
                  labFiles: [...(test.labFiles || []), ...(labFiles || [])],

                  // ✅ update only if value exists
                  labDescription:
                    labDescription !== undefined && labDescription !== ""
                      ? labDescription
                      : test.labDescription,
                }
              : test,
          );

          return {
            ...appt,
            testRecommendations: updatedTests,
          };
        });

        if (
          state.latestAppointment &&
          state.latestAppointment.appointmentId === appointmentId
        ) {
          const tests = state.latestAppointment.testRecommendations || [];

          state.latestAppointment.testRecommendations = tests.map((test) =>
            test.testid === testId
              ? {
                  ...test,
                  labFiles: [...(test.labFiles || []), ...(labFiles || [])],
                  labDescription:
                    labDescription !== undefined && labDescription !== ""
                      ? labDescription
                      : test.labDescription,
                }
              : test,
          );
        }

        
      })

      .addCase(cancelTestFromDb.rejected, (state, action) => {
        console.warn("❌ Cancel test thunk rejected:", action.payload);
      })

      .addCase(saveAppointmentsToDb.pending, (state) => {
        state.saveStatus = "loading";
        state.saveError = null;
      })
      .addCase(saveAppointmentsToDb.fulfilled, (state, action) => {
        state.saveStatus = "succeeded";
        state.saveError = null;

        const appointments = action.payload?.appointments || [];

        state.allAppointments = appointments;
        state.latestAppointment =
          appointments.length > 0
            ? appointments[appointments.length - 1]
            : null;
      })
      .addCase(saveAppointmentsToDb.rejected, (state, action) => {
        state.saveStatus = "failed";

        state.saveError =
          action.payload ||
          action.error?.message ||
          "Failed to book appointment";
      })

      // ✅ Add your cancel thunk here
      .addCase(cancelAppointmentFromDb.fulfilled, (state, action) => {
        const id = action.payload;
        state.allAppointments = state.allAppointments.filter(
          (a) => a.appointmentId !== id,
        );
      })
      .addCase(cancelAppointmentFromDb.rejected, (state, action) => {
        console.error("❌ Failed to cancel appointment:", action.payload);
      });
  },
});

export const {
  setAppointments,
  addnewAppointment,
  cancelAppointment,
  cancelTest,
  rescheduleAppointment,
  updateAppointmentStatus,
  addPrescription,
  testRecommendation,
  referToAnotherDoctor,
  updateTestRecommendation,
  updateReferredUser,
  assignUserToTest,
  updatedAppointment,
} = appointmentsSlice.actions;

export default appointmentsSlice.reducer;
